/**
 * Feature E07-F05 — Evidence Drawer (citation verification) React view.
 *
 * 1:1 port of the prototype `../EER app/prototype/assets/research.proto.js`
 * (openEvidence L1095-1131, requestMoreInfo L1138-1161, verdictChip L840,
 * gradeLabel L1133) over the v2 evidence-drawer DTO. The Review screen (E07-F04)
 * opens this drawer to show the proof base behind ONE scored long-list row.
 *
 * Tasks realised here:
 *   T01 — the slide-over shell: composes the shared `Drawer` primitive (slide-in
 *         from the right over a dimming scrim, focus-trap, Esc-to-close, return-
 *         focus). No bespoke a11y — the Drawer owns it.
 *   T02 — renders the evidence JSONB with verdict colours: each of the 6 criteria
 *         is a card coloured by its verdict (pass green / partial amber / fail red
 *         / unclear slate), with the verbatim quote + source URL (amber fallbacks
 *         when a PASS/PARTIAL has no quote / no source URL).
 *   T03 — the "Request more info" button: re-checks the weakest criterion against
 *         the company's own website (pages fetched + read by the configured AI
 *         model, E06-F05-T09) to upgrade it to a cited PASS; toasts one of four distinct outcomes
 *         (upgraded / nothing new found / already fully verified / degraded) —
 *         T07: "nothing found" is an error toast, never a success.
 *   T04 — shows the weighted-contribution per criterion (weight × match-factor)
 *         and the division/portfolio note for an extracted-division entity, plus a
 *         Σ(contribution) total that sanity-checks against the stored score; for
 *         an extracted division the size/founded cards carry `unitScopeNote`
 *         (graded on the unit, not the parent — AC3).
 *   T06 — the trust banner (verified green vs needs-human amber) at the top of the
 *         body, copy verbatim from the contract.
 *   E07-F11-T05 — a row verified by Jev (`drawer.jev`): per criterion the verdict + Jev
 *         confidence, the source page with "Open page", the code-found fragment labelled
 *         «фрагмент найден поиском, не цитата модели» and search-snippet sources labelled
 *         «по поисковой выдаче, не с сайта компании»; a panel with every page Jev read. Haiku
 *         rows (no `drawer.jev`) render exactly as before.
 *
 * Presentation rules (colour classes, formatted strings) live in the DOM-free,
 * unit-tested `./evidenceDrawerModel.ts`; the wire shapes + hooks come from
 * `./researchEvidenceApi.ts`; the canonical criterion order/labels/weights come
 * from `@eer/shared`. No `innerHTML` — every cell is React-escaped. Colours go
 * through brand-token Tailwind utilities (design.md "Don't" — no literal hexes,
 * except the gold accent `#D4AF37` already used across the Review screen).
 */
import { type JSX } from 'react';
import {
  INSUFFICIENT_DATA_LABEL,
  describeInsufficientData,
  type EvidenceCriterionRow,
  type EvidenceDrawer as EvidenceDrawerDto,
  type EvidenceJev,
  type EvidenceSearchSource,
  type ScoreExplanation,
} from '@eer/shared';
import { Drawer } from '../ui/index.js';
import { useToast } from '../ui/ToastContext.js';
import {
  verdictStyle,
  trustStyle,
  formatContribution,
  formatWeight,
  subheaderSegments,
  contributionMatchesScore,
  requestMoreInfoMessage,
  formatConfidence,
  pageCaption,
  isOpenableUrl,
} from './evidenceDrawerModel.js';
import { useEvidenceDrawer, useRequestMoreInfo } from './researchEvidenceApi.js';
import { companyAriaLabel } from './CompanyLabel.js';
import { ScoreExplainPanel } from './ScoreExplain.js';

// ---------------------------------------------------------------------------
// public props
// ---------------------------------------------------------------------------

export interface EvidenceDrawerProps {
  /** The research_request the long-list row belongs to (scopes the fetch). */
  readonly requestId: number;
  /** The research_companies row whose evidence to show; null → drawer closed. */
  readonly rowId: number | null;
  /** Close request (Esc, scrim, close cross, or the footer Close button). */
  readonly onClose: () => void;
  /**
   * E07-F04-T21 — the score explanation of the open row (headline "N% = X of 100
   * weighted points…", "not proven ≠ does not match", why the row is on the list).
   * The Review Score cell opens THIS drawer, so the breakdown lands in a slide-over
   * that is always in view — the old inline table row opened off-screen.
   */
  readonly explanation?: ScoreExplanation | null;
}

// ---------------------------------------------------------------------------
// trust banner (T06)
// ---------------------------------------------------------------------------

function TrustBanner({ drawer }: { drawer: EvidenceDrawerDto }): JSX.Element {
  const { tone, message } = drawer.trust;
  const style = trustStyle(tone);
  return (
    <div
      data-testid="evidence-trust-banner"
      data-trust-tone={tone}
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${style.containerClass}`}
    >
      <i className={`fa-solid ${style.icon} mt-0.5`} aria-hidden />
      <div>
        <b className="block" data-testid="evidence-trust-heading">
          {/* E07-F11-T05 — a Jev row is confidence-verified, not citation-verified */}
          {drawer.jev && tone === 'verified' ? 'Verified by Jev' : style.heading}
        </b>
        <span className="text-[13px] leading-snug opacity-90">{message}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// E07-F01-T11 — "insufficient data" banner (no evidence ≠ checked and unfit)
// ---------------------------------------------------------------------------

function InsufficientDataBanner({ drawer }: { drawer: EvidenceDrawerDto }): JSX.Element | null {
  const d = drawer.insufficientData;
  if (!d) return null;
  return (
    <div
      data-testid="evidence-insufficient-data"
      data-reason={d.reason}
      className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800"
    >
      <i className="fa-solid fa-circle-question mt-0.5" aria-hidden />
      <div>
        <b className="block">{INSUFFICIENT_DATA_LABEL}</b>
        <span className="text-[13px] leading-snug opacity-90">{describeInsufficientData(d)}</span>
        <span className="block text-[12px] opacity-75">
          No score and no automatic grade — this is not a “does not fit”.
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// division / portfolio note (T04)
// ---------------------------------------------------------------------------

function DivisionNote({ drawer }: { drawer: EvidenceDrawerDto }): JSX.Element | null {
  const note = drawer.divisionNote;
  if (!note) return null;
  return (
    <div
      data-testid="evidence-division-note"
      data-entity-type={note.entityType}
      className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800"
    >
      <i className="fa-solid fa-sitemap" aria-hidden />
      <span>
        Scored as an extracted entity — <b>{note.label}</b>. The grade reflects the in-scope
        division/offering, not the whole parent: Size and Founded are judged on the unit itself,
        which is why they can be PARTIAL instead of failing on the parent&apos;s figures.
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// E07-F11-T05 — Jev evidence: source pages, confidence, search-snippet mark
// ---------------------------------------------------------------------------

/** "Open page" — a source page in a new tab (http(s) only; else the bare address). */
function OpenPageLink({ url, testId }: { url: string; testId: string }): JSX.Element {
  if (!isOpenableUrl(url)) {
    return (
      <span data-testid={testId} className="truncate text-[12px] text-slate-500" title={url}>
        {url}
      </span>
    );
  }
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <span className="max-w-[16rem] truncate text-[12px] text-slate-600" title={url}>
        {pageCaption(url)}
      </span>
      <a
        data-testid={testId}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex shrink-0 items-center gap-1 rounded border border-border bg-white px-2 py-0.5 text-[11px] font-semibold text-blue-600 hover:bg-blue-50"
      >
        <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden />
        Open page
      </a>
    </span>
  );
}

/** Snippet sources, labelled «по поисковой выдаче, не с сайта компании» (spec §5.8). */
function SearchSourceNote({
  source,
  testId,
}: {
  source: EvidenceSearchSource;
  testId: string;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="mt-2 rounded border border-sky-200 bg-sky-50 px-2 py-1.5 text-[12px] text-sky-800"
    >
      <span className="font-semibold" data-testid={`${testId}-label`}>
        <i className="fa-solid fa-magnifying-glass mr-1" aria-hidden />
        {source.label}
      </span>
      <ul className="mt-1 space-y-1">
        {source.urls.map((u) => (
          <li key={u}>
            <OpenPageLink url={u} testId={`${testId}-url`} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The pages Jev read + its grade confidence + the site-fallback search snippets. */
function JevSourcesPanel({ jev }: { jev: EvidenceJev }): JSX.Element {
  return (
    <section
      data-testid="evidence-jev-sources"
      data-basis={jev.basis}
      className="rounded-lg border border-border bg-slate-50 px-4 py-3 text-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <b className="text-slate-800">Checked by Jev</b>
        <span
          data-testid="evidence-jev-grade-confidence"
          className="rounded bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200"
        >
          grade confidence {formatConfidence(jev.gradeConfidence)}
        </span>
      </div>
      {jev.pages.length > 0 ? (
        <>
          <p className="mt-2 text-[12px] text-slate-500">Source pages the decision was taken on:</p>
          <ul className="mt-1 space-y-1" data-testid="evidence-jev-pages">
            {jev.pages.map((u) => (
              <li key={u}>
                <OpenPageLink url={u} testId="evidence-jev-page" />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="mt-2 text-[12px] text-slate-500" data-testid="evidence-jev-no-pages">
          The company website gave no readable pages.
        </p>
      )}
      {jev.searchUrls.length > 0 ? (
        <SearchSourceNote
          source={{ label: jev.searchLabel, urls: jev.searchUrls }}
          testId="evidence-jev-search"
        />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// one criterion card (T02 colour-by-verdict + T04 weighted contribution)
// ---------------------------------------------------------------------------

function CriterionCard({ row, jev }: { row: EvidenceCriterionRow; jev: boolean }): JSX.Element {
  // E07-F11-T05 — a Jev row: confidence rule instead of citation-or-reject (no quote by design).
  if (jev) return <JevCriterionCard row={row} />;
  const style = verdictStyle(row.verdict);
  // A PASS/PARTIAL claim — either shown as such, or downgraded to UNCLEAR by the
  // API for lack of a verbatim quote (citation-or-reject, AC4).
  const downgradedFrom = row.downgradedFrom ?? null;
  const claimed = row.verdict === 'pass' || row.verdict === 'partial' || downgradedFrom != null;
  return (
    <li
      data-testid={`evidence-criterion-${row.key}`}
      data-verdict={row.verdict}
      data-downgraded-from={downgradedFrom ?? undefined}
      className={`rounded-lg border border-l-4 border-border bg-white px-4 py-3 ${style.accentClass}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-slate-800" data-testid={`evidence-label-${row.key}`}>
          {row.label}
        </span>
        <span
          data-testid={`evidence-verdict-${row.key}`}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${style.badgeClass}`}
        >
          <i className={`fa-solid ${style.icon}`} aria-hidden />
          {row.verdictLabel}
        </span>
      </div>

      {/* verbatim quote (T02) — mono, left border coloured by verdict; amber
          warning (+ downgrade note) when a PASS/PARTIAL claim has no quote */}
      {row.hasQuote ? (
        <blockquote
          data-testid={`evidence-quote-${row.key}`}
          data-quote-verdict={row.verdict}
          className={`mt-2 whitespace-pre-wrap break-words border-l-4 pl-3 font-mono text-[12px] text-slate-700 ${style.quoteBorderClass}`}
        >
          “{row.quote}”
        </blockquote>
      ) : claimed ? (
        <p
          data-testid={`evidence-noquote-${row.key}`}
          className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[12px] text-amber-700"
        >
          <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden />
          No verbatim quote captured
          {downgradedFrom ? (
            <>
              {' '}
              — verdict downgraded from{' '}
              <b data-testid={`evidence-downgraded-${row.key}`}>
                {downgradedFrom.toUpperCase()}
              </b>{' '}
              to {row.verdictLabel}
            </>
          ) : null}
          . Verify the source manually before publish.
        </p>
      ) : null}

      {/* T04 AC3 — extracted division: size/founded judged on the unit, not the parent */}
      {row.unitScopeNote ? (
        <p
          data-testid={`evidence-unit-scope-${row.key}`}
          className="mt-2 flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[12px] text-amber-800"
        >
          <i className="fa-solid fa-sitemap mt-0.5" aria-hidden />
          <span>{row.unitScopeNote}</span>
        </p>
      ) : null}

      {/* source URL (T02) — amber fallback when a claimed row has no URL */}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        {row.hasUrl ? (
          <a
            data-testid={`evidence-url-${row.key}`}
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 truncate text-[12px] text-blue-600 hover:underline"
            title={row.url}
          >
            <i className="fa-solid fa-link" aria-hidden />
            <span className="max-w-[18rem] truncate">{row.url}</span>
          </a>
        ) : claimed ? (
          <span
            data-testid={`evidence-nourl-${row.key}`}
            className="text-[12px] italic text-amber-600"
          >
            <i className="fa-solid fa-link-slash mr-1" aria-hidden />
            No source URL
          </span>
        ) : (
          <span className="text-[12px] text-slate-400">No source needed (criterion not met)</span>
        )}

        {/* weighted contribution (T04) */}
        <span
          data-testid={`evidence-contribution-${row.key}`}
          data-contribution={row.contribution}
          data-weight={row.weight}
          title={`${formatWeight(row)} of the active profile`}
          className="whitespace-nowrap rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
        >
          {formatContribution(row)}
        </span>
      </div>
    </li>
  );
}

/**
 * E07-F11-T05 — one criterion of a Jev-verified row: verdict + Jev's confidence, the source page
 * with "Open page", the code-found fragment labelled «фрагмент найден поиском, не цитата модели»,
 * and — when the criterion rests on search snippets — the «по поисковой выдаче…» source.
 */
function JevCriterionCard({ row }: { row: EvidenceCriterionRow }): JSX.Element {
  const style = verdictStyle(row.verdict);
  const fragment = row.fragment ?? null;
  const searchSource = row.searchSource ?? null;
  return (
    <li
      data-testid={`evidence-criterion-${row.key}`}
      data-verdict={row.verdict}
      data-engine="jev"
      className={`rounded-lg border border-l-4 border-border bg-white px-4 py-3 ${style.accentClass}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-slate-800" data-testid={`evidence-label-${row.key}`}>
          {row.label}
        </span>
        <span className="inline-flex items-center gap-2">
          <span
            data-testid={`evidence-confidence-${row.key}`}
            data-confidence={row.confidence ?? ''}
            title="Probability Jev gave this verdict"
            className="whitespace-nowrap text-[11px] text-slate-500"
          >
            confidence {formatConfidence(row.confidence)}
          </span>
          <span
            data-testid={`evidence-verdict-${row.key}`}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${style.badgeClass}`}
          >
            <i className={`fa-solid ${style.icon}`} aria-hidden />
            {row.verdictLabel}
          </span>
        </span>
      </div>

      {/* a verbatim quote only exists when a later Haiku "Request more info" added one */}
      {row.hasQuote ? (
        <blockquote
          data-testid={`evidence-quote-${row.key}`}
          className={`mt-2 whitespace-pre-wrap break-words border-l-4 pl-3 font-mono text-[12px] text-slate-700 ${style.quoteBorderClass}`}
        >
          “{row.quote}”
        </blockquote>
      ) : null}

      {/* code-found fragment — a hint, never a model quote (spec §5.4 п.4) */}
      {fragment ? (
        <div data-testid={`evidence-fragment-${row.key}`} className="mt-2">
          <span
            data-testid={`evidence-fragment-label-${row.key}`}
            className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500"
          >
            <i className="fa-solid fa-magnifying-glass" aria-hidden />
            {fragment.label}
          </span>
          <p className="mt-1 whitespace-pre-wrap break-words border-l-2 border-dashed border-slate-300 pl-3 text-[12px] italic text-slate-600">
            {fragment.text}
          </p>
        </div>
      ) : null}

      {row.unitScopeNote ? (
        <p
          data-testid={`evidence-unit-scope-${row.key}`}
          className="mt-2 flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[12px] text-amber-800"
        >
          <i className="fa-solid fa-sitemap mt-0.5" aria-hidden />
          <span>{row.unitScopeNote}</span>
        </p>
      ) : null}

      {searchSource ? (
        <SearchSourceNote source={searchSource} testId={`evidence-search-source-${row.key}`} />
      ) : null}

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        {row.hasUrl ? (
          <OpenPageLink url={row.url} testId={`evidence-url-${row.key}`} />
        ) : searchSource ? (
          <span />
        ) : (
          <span
            data-testid={`evidence-nourl-${row.key}`}
            className="text-[12px] italic text-slate-400"
          >
            No source page
          </span>
        )}
        <span
          data-testid={`evidence-contribution-${row.key}`}
          data-contribution={row.contribution}
          data-weight={row.weight}
          title={`${formatWeight(row)} of the active profile`}
          className="whitespace-nowrap rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
        >
          {formatContribution(row)}
        </span>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// navy-header subtitle (T01 chrome + T04 subheader content)
// ---------------------------------------------------------------------------

/**
 * "<resolvedEntity> · <gradeLabel> · score <n>" — rendered INSIDE the navy header
 * under the company name (prototype #evidence-sub), not in the scrollable body.
 */
function EvidenceSubheader({ drawer }: { drawer: EvidenceDrawerDto }): JSX.Element {
  const segments = subheaderSegments(drawer);
  return (
    <p
      data-testid="evidence-subheader"
      data-grade={drawer.grade ?? ''}
      data-needs-human={drawer.needsHuman ? 'true' : 'false'}
      data-check-state={drawer.checkState}
      className="text-xs text-white/70"
    >
      {segments.map((seg, i) => (
        <span key={seg + String(i)}>
          {i > 0 ? <span className="mx-1.5 text-white/40">·</span> : null}
          <span className={i === 0 ? 'font-medium text-white/90' : ''}>{seg}</span>
        </span>
      ))}
      {drawer.divisionNote ? (
        <span>
          <span className="mx-1.5 text-white/40">·</span>
          <span className="text-amber-300">{drawer.divisionNote.label}</span>
        </span>
      ) : null}
      {/* E07-F01-T04 — the one-line grade rationale (same text as the Review tooltip). */}
      {drawer.rationale ? (
        <span data-testid="evidence-rationale" className="mt-0.5 block text-white/80">
          {drawer.rationale}
        </span>
      ) : null}
    </p>
  );
}

// ---------------------------------------------------------------------------
// drawer body (loaded)
// ---------------------------------------------------------------------------

function DrawerBody({
  drawer,
  onRequestMore,
  requesting,
}: {
  drawer: EvidenceDrawerDto;
  onRequestMore: () => void;
  requesting: boolean;
}): JSX.Element {
  const scoreOk = contributionMatchesScore(drawer);
  return (
    <div className="space-y-4" data-testid="evidence-drawer-content">
      <TrustBanner drawer={drawer} />
      <InsufficientDataBanner drawer={drawer} />
      <DivisionNote drawer={drawer} />
      {drawer.jev ? <JevSourcesPanel jev={drawer.jev} /> : null}

      {/* the 6 criteria, colour-coded by verdict (T02), with contributions (T04) */}
      <ul className="space-y-2.5" data-testid="evidence-criteria">
        {drawer.criteria.map((row) => (
          <CriterionCard key={row.key} row={row} jev={drawer.jev != null} />
        ))}
      </ul>

      {/* Σ(contribution) total — sanity-checks against the stored score (T04) */}
      <div
        data-testid="evidence-contribution-total"
        data-total={drawer.contributionTotal}
        className="flex items-center justify-between rounded-md bg-slate-50 px-4 py-2 text-sm"
      >
        <span className="font-medium text-slate-600">Weighted total (Σ contributions)</span>
        <span className="font-bold text-slate-900">
          {drawer.contributionTotal}
          {drawer.score != null ? (
            <span className="ml-2 text-xs font-normal text-slate-400">
              stored score {drawer.score}
            </span>
          ) : null}
        </span>
      </div>
      {!scoreOk ? (
        <p data-testid="evidence-score-mismatch" className="text-[12px] text-amber-600">
          <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden />
          The computed Σ differs from the stored score — re-run scoring before publish.
        </p>
      ) : null}

      {/* Request more info (T03) */}
      <button
        type="button"
        data-testid="evidence-request-more"
        disabled={requesting}
        onClick={onRequestMore}
        title="Re-check the weakest criterion against the company's own website to upgrade it with first-party evidence"
        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-50"
      >
        <i
          className={`fa-solid ${requesting ? 'fa-spinner fa-spin' : 'fa-magnifying-glass-plus'}`}
          aria-hidden
        />
        {requesting ? 'Searching deeper…' : 'Request more info'}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// root view (T01 — composes the Drawer primitive)
// ---------------------------------------------------------------------------

export function EvidenceDrawer({
  requestId,
  rowId,
  onClose,
  explanation = null,
}: EvidenceDrawerProps): JSX.Element {
  const open = rowId != null;
  const toast = useToast();
  const query = useEvidenceDrawer(requestId, rowId, open);
  const requestMore = useRequestMoreInfo(requestId);

  const drawer = query.data?.drawer;

  const handleRequestMore = (): void => {
    if (rowId == null) return;
    requestMore.mutate(rowId, {
      onSuccess: (res) => {
        const message = requestMoreInfoMessage(res);
        if (message.tone === 'success') toast.success(message.text);
        else toast.error(message.text);
      },
      onError: (err) => {
        toast.error(err.message || 'Request more info failed.');
      },
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      // Navy header = the selected company's name + its subheader (prototype
      // #evidence-company / #evidence-sub). "Evidence" only while the DTO loads.
      title={drawer ? companyAriaLabel(drawer.name) : 'Evidence'}
      subtitle={drawer ? <EvidenceSubheader drawer={drawer} /> : 'Per-criterion evidence'}
      side="right"
      width="xl"
      testId="evidence-drawer"
      footer={
        <button
          type="button"
          data-testid="evidence-drawer-close-btn"
          onClick={onClose}
          className="rounded-md border border-border bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          Close
        </button>
      }
    >
      {/* E07-F04-T21 — the full score breakdown (weighted factors that add up to the
        number on screen + why the row is on the list). Derived from the list row, so
        it is shown even while the evidence DTO loads; the quotes follow below. */}
      {explanation && rowId != null ? (
        <div className="mb-4">
          <ScoreExplainPanel explanation={explanation} rowId={rowId} />
        </div>
      ) : null}
      {query.isLoading ? (
        <div data-testid="evidence-loading" className="py-12 text-center text-sm text-slate-400">
          <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
          Loading evidence…
        </div>
      ) : query.isError ? (
        <div data-testid="evidence-error" className="py-12 text-center text-sm text-red-500">
          <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden />
          {query.error?.message || 'Could not load the evidence for this company.'}
        </div>
      ) : drawer ? (
        <DrawerBody
          drawer={drawer}
          onRequestMore={handleRequestMore}
          requesting={requestMore.isPending}
        />
      ) : null}
    </Drawer>
  );
}

export default EvidenceDrawer;
